import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  CalendarBlank,
  CaretLeft,
  CaretRight,
  Clock,
  ArrowSquareOut,
  VideoCamera,
  Users as UsersIcon,
  MapPin,
  Link as LinkIcon
} from '@phosphor-icons/react';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const CalendarPage = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendlyEvents, setCalendlyEvents] = useState([]);
  const [calendlyUser, setCalendlyUser] = useState(null);
  const [calendlyEventTypes, setCalendlyEventTypes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadCalendly(); }, []);
  useEffect(() => { loadCalendlyEvents(); }, [currentDate]);

  const loadCalendly = async () => {
    try {
      const [u, types] = await Promise.all([
        api.getCalendlyUser(),
        api.getCalendlyEventTypes(),
      ]);
      setCalendlyUser(u);
      setCalendlyEventTypes(types);
    } catch (e) {
      console.error('Calendly load error:', e);
    }
    loadCalendlyEvents();
  };

  const loadCalendlyEvents = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const minDate = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const maxDate = new Date(year, month + 2, 0).toISOString().split('T')[0];
      const events = await api.getCalendlyEvents(minDate, maxDate);
      setCalendlyEvents(events);
    } catch (e) {
      console.error('Calendly events error:', e);
    }
    setLoading(false);
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const getDayCalendlyEvents = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendlyEvents.filter(ev => {
      const evDate = ev.start_time?.split('T')[0];
      return evDate === dateStr;
    });
  };

  const selectedDateCalendly = selectedDate ? calendlyEvents.filter(ev => ev.start_time?.split('T')[0] === selectedDate) : [];

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(todayStr); };

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="calendar-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Lab Calendar</h1>
          <p className="text-slate-400 mt-1">Schedule and track events via Calendly</p>
        </div>
        {calendlyUser?.scheduling_url && (
          <a href={calendlyUser.scheduling_url} target="_blank" rel="noopener noreferrer">
            <Button className="btn-primary flex items-center gap-2" data-testid="calendly-book-btn">
              <ArrowSquareOut size={16} /> Book via Calendly
            </Button>
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-3 card-flat p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="heading-section">{MONTHS[month]} {year}</h2>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="btn-secondary px-2 py-1" onClick={prevMonth} data-testid="prev-month">
                <CaretLeft size={16} />
              </Button>
              <Button size="sm" variant="outline" className="btn-secondary px-3 py-1 text-xs" onClick={goToday}>Today</Button>
              <Button size="sm" variant="outline" className="btn-secondary px-2 py-1" onClick={nextMonth} data-testid="next-month">
                <CaretRight size={16} />
              </Button>
            </div>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-px mb-1">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wider py-2">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-lg overflow-hidden">
            {calendarDays.map((day, i) => {
              if (!day) return <div key={`empty-${i}`} className="bg-slate-800 min-h-[90px]" />;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const dayCalendly = getDayCalendlyEvents(day);
              const isToday = dateStr === todayStr;
              const isSelected = dateStr === selectedDate;

              return (
                <div
                  key={day}
                  className={`bg-slate-800 min-h-[90px] p-2 cursor-pointer transition-all hover:bg-indigo-900/30/30 ${isSelected ? 'ring-2 ring-teal-500 ring-inset' : ''}`}
                  onClick={() => setSelectedDate(dateStr)}
                  data-testid={`cal-day-${day}`}
                >
                  <span className={`text-sm font-medium inline-flex items-center justify-center w-7 h-7 rounded-full ${isToday ? 'bg-indigo-900/300 text-white' : 'text-slate-200'}`}>
                    {day}
                  </span>
                  <div className="mt-1 space-y-0.5">
                    {dayCalendly.slice(0, 3).map((ev, idx) => (
                      <div key={idx} className="text-[10px] px-1.5 py-0.5 rounded truncate bg-blue-100 text-indigo-400">
                        {formatTime(ev.start_time)} {ev.name}
                      </div>
                    ))}
                    {dayCalendly.length > 3 && (
                      <span className="text-[10px] text-slate-400">+{dayCalendly.length - 3} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Panel */}
        <div className="space-y-4">
          {/* Selected Day Events */}
          <div className="card-flat p-4">
            <h3 className="heading-card mb-3">
              {selectedDate ? new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'Select a date'}
            </h3>
            {selectedDate ? (
              selectedDateCalendly.length > 0 ? (
                <div className="space-y-2">
                  {selectedDateCalendly.map(ev => (
                    <div key={ev.uri} className="p-3 border border-blue-200 rounded-lg bg-blue-900/20/30 hover:border-blue-300 transition-colors" data-testid={`calendly-event-${ev.uri?.split('/').pop()}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <VideoCamera size={14} className="text-blue-500" />
                        <span className="font-medium text-sm text-blue-800">{ev.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ev.status === 'active' ? 'bg-blue-100 text-indigo-400' : 'bg-slate-100 text-slate-400'}`}>
                          {ev.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-400 ml-5">
                        <Clock size={12} /> {formatTime(ev.start_time)} - {formatTime(ev.end_time)}
                      </div>
                      {ev.location?.type && (
                        <div className="flex items-center gap-1 text-xs text-slate-400 ml-5 mt-0.5">
                          <MapPin size={10} /> {ev.location.type}
                          {ev.location.join_url && (
                            <a href={ev.location.join_url} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline ml-1">
                              <LinkIcon size={10} className="inline" /> Join
                            </a>
                          )}
                        </div>
                      )}
                      {ev.invitees?.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-slate-400 ml-5 mt-0.5">
                          <UsersIcon size={10} /> {ev.invitees.map(inv => inv.name || inv.email).join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400">
                  <CalendarBlank size={32} className="mx-auto mb-2 opacity-40" />
                  <p className="text-xs">No events on this day</p>
                </div>
              )
            ) : (
              <p className="text-xs text-slate-400">Click a date on the calendar</p>
            )}
          </div>

          {/* Calendly Event Types */}
          {calendlyEventTypes.length > 0 && (
            <div className="card-flat p-4">
              <h3 className="heading-card mb-3 flex items-center gap-2">
                <VideoCamera size={14} className="text-blue-500" /> Calendly Booking
              </h3>
              <div className="space-y-2">
                {calendlyEventTypes.filter(et => et.active).map(et => (
                  <a
                    key={et.uri}
                    href={et.scheduling_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 border border-blue-200 rounded-lg hover:border-blue-400 hover:bg-blue-900/20 transition-all"
                    data-testid={`event-type-${et.name}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-800">{et.name}</p>
                        <p className="text-xs text-slate-400">{et.duration} min</p>
                      </div>
                      <ArrowSquareOut size={14} className="text-blue-400" />
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming events summary */}
          <div className="card-flat p-4">
            <h3 className="heading-card mb-3">Upcoming</h3>
            <div className="space-y-2">
              {calendlyEvents.filter(ev => ev.status === 'active' && new Date(ev.start_time) >= new Date()).slice(0, 5).map(ev => (
                <div key={ev.uri} className="flex items-center gap-2 text-sm">
                  <div className="w-2 h-2 rounded-full bg-blue-900/200 flex-shrink-0" />
                  <span className="truncate flex-1 text-blue-800">{ev.name}</span>
                  <span className="text-xs text-slate-400 flex-shrink-0">{ev.start_time?.split('T')[0]}</span>
                </div>
              ))}
              {calendlyEvents.filter(ev => ev.status === 'active').length === 0 && (
                <p className="text-xs text-slate-400">No upcoming events</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
